import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import CryptoJS from 'crypto-js'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Convert ArrayBuffer to WordArray for crypto-js
 */
function bufferToWordArray(buffer: ArrayBuffer): CryptoJS.lib.WordArray {
  const view = new Uint8Array(buffer);
  const words: number[] = [];
  for (let i = 0; i < view.length; i++) {
    if (i % 4 === 0) {
      words.push(0);
    }
    words[words.length - 1] |= view[i] << (24 - (i % 4) * 8);
  }
  return CryptoJS.lib.WordArray.create(words, view.length);
}

/**
 * Calculate MD5 hash using crypto-js
 */
export function calculateMD5(buffer: ArrayBuffer): string {
  const wordArray = bufferToWordArray(buffer);
  return CryptoJS.MD5(wordArray).toString();
}

/**
 * Calculate SHA1 hash using crypto-js
 */
export function calculateSHA1(buffer: ArrayBuffer): string {
  const wordArray = bufferToWordArray(buffer);
  return CryptoJS.SHA1(wordArray).toString();
}

/**
 * Calculate SHA-256 hash (using Web Crypto API for performance)
 */
export async function calculateSHA256(buffer: ArrayBuffer): Promise<string> {
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Calculate MD5, SHA1, and SHA256 hashes entirely client-side
 */
export async function calculateFileHashes(buffer: ArrayBuffer): Promise<{ md5: string; sha1: string; sha256: string }> {
  try {
    // All calculations are done client-side using crypto-js (and Web Crypto for SHA256)
    const md5Hash = calculateMD5(buffer);
    const sha1Hash = calculateSHA1(buffer);
    const sha256Hash = await calculateSHA256(buffer);

    console.debug('File hashes calculated successfully:', { 
      md5: md5Hash.substring(0, 8) + '...', 
      sha1: sha1Hash.substring(0, 8) + '...',
      sha256: sha256Hash.substring(0, 8) + '...'
    });

    return {
      md5: md5Hash,
      sha1: sha1Hash,
      sha256: sha256Hash
    };
  } catch (error) {
    console.error('calculateFileHashes error:', error);
    throw error;
  }
}

/**
 * Copy text to the clipboard with a fallback
 */
export async function copyToClipboard(text: string): Promise<void> {
  try {
    if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
  } catch {
    return;
  }
}



